import { apiClient, getUserId } from "./authService";

interface CommunityData {
  [key: string]: any;
}

export const getAllCommunity = async (businessId?: string): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");
    
    const response = await apiClient.get("/community/all", {
      params: businessId ? { businessId } : undefined,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCommunityById = async (id: string): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.get(`/community/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCommunity = async (communityData: CommunityData): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.post("/community/create", communityData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCommunity = async (
  id: string,
  communityData: CommunityData
): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.patch(`/community/update/${id}`, communityData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCommunity = async (id: string): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.delete(`/community/delete/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addCommunityMember = async (
  id: string,
  dto: { organizationUserId: string }
): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.post(`/community/${id}/join`, dto);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeCommunityMember = async (
  id: string,
  dto: { organizationUserId: string }
): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.post(`/community/${id}/leave`, dto);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCommunityMembers = async (id: string): Promise<any> => {
  try {
    const userId = getUserId();
    if (!userId) throw new Error("User ID not found. Please log in again.");

    const response = await apiClient.get(`/community/${id}/members`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const extractMemberIds = (payload: CommunityData): string[] => {
  const raw =
    payload?.organizationUserIds ??
    payload?.organizationUserId ??
    payload?.client_ids ??
    payload?.clientIds ??
    payload?.memberIds ??
    payload?.member_ids;

  const ids = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return ids
    .filter((id) => id !== undefined && id !== null && String(id).trim() !== "")
    .map((id) => String(id));
};

const extractExistingMemberIds = (membersResponse: any): Set<string> => {
  const members =
    (Array.isArray(membersResponse) && membersResponse) ||
    membersResponse?.members ||
    membersResponse?.data ||
    [];

  const list = Array.isArray(members) ? members : [];
  const ids = new Set<string>();

  for (const member of list) {
    const rawId =
      member?.organizationUserId ??
      member?.organization_user_id ??
      member?.organizationUserID ??
      member?.user_id ??
      member?.client_id ??
      member?.member_id ??
      member?.id ??
      member?._id;

    if (rawId !== undefined && rawId !== null && String(rawId).trim() !== "") {
      ids.add(String(rawId));
    }
  }

  return ids;
};

// Backward-compatible helpers used by existing UI
export const AssignClients = async (
  id: string,
  payload: CommunityData
): Promise<any> => {
  try {
    const memberIds = extractMemberIds(payload);
    if (memberIds.length === 0) return { addedCount: 0 };

    let existing = new Set<string>();
    try {
      const current = await getCommunityMembers(id);
      existing = extractExistingMemberIds(current);
    } catch {
      // best-effort; proceed with adding and let API decide duplicates
    }

    const toAdd = memberIds.filter((memberId) => !existing.has(String(memberId)));
    if (toAdd.length === 0) return { addedCount: 0 };

    const results = await Promise.allSettled(
      toAdd.map((organizationUserId) =>
        addCommunityMember(id, { organizationUserId: String(organizationUserId) })
      )
    );

    const rejected = results.filter((r) => r.status === "rejected");
    if (rejected.length) {
      const error: any = new Error(
        "Failed to add one or more members to the community."
      );
      error.details = rejected;
      throw error;
    }

    return { addedCount: toAdd.length };
  } catch (error) {
    throw error;
  }
};
export const GetAssignedClients = async (
  id: string
): Promise<any> => {
  try {
    const response = await apiClient.get(`/community/${id}/members`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const deleteMember = async (id: string, memberId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/community/${id}/leave`, {
      organizationUserId: String(memberId),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
